import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { FontSize } from '../../../../../constants/FontSize';
import { PageUrl } from '../../../../../constants/PageUrl';
import Color from '../../../../../styles/Color';
import LineButton from '../../../atoms/LineButton';
import MainButton from '../../../atoms/MainButton';
import styles from './index.module.scss';

const REVEAL_SCROLL_RATIO = 0.6;
const IDLE_DELAY_MS = 300;
const CONTACT_CTA_POSITION = 'daycare_sticky_contact_cta';
const LINE_CTA_POSITION = 'daycare_sticky_line_cta';

const handleContactClick = () => {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: 'daycare_sticky_cta_click', cta_position: CONTACT_CTA_POSITION });
};

type Props = {
  /**
   * 'contact': 見学・体験予約ページへの遷移ボタン（既定）。ヘッダーの「見学・体験予約」ボタンと同じ行き先・見た目に揃える。
   * 'line': 公式LINEへの直リンク（体験クラスLPなど、既にLINE誘導が機能しているページ向けの従来挙動）
   */
  variant?: 'contact' | 'line';
  label?: string;
};

export const StickyLineCta = ({ variant = 'contact', label }: Props) => {
  const [visible, setVisible] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasFiredImpressionRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || hasFiredImpressionRef.current) {
      return;
    }

    const el = wrapperRef.current;
    if (!el) {
      return;
    }

    const ctaPosition = variant === 'line' ? LINE_CTA_POSITION : CONTACT_CTA_POSITION;

    // `visible`切替と同時に発火すると、`transform 0.3s`のスライドインアニメーション開始時点
    // （CTAがまだ画面外）でimpressionを確定してしまう。実際に表示し終える`transitionend`
    // まで待つことで、アニメーション中の離脱による過大計上を防ぐ。
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== 'transform') {
        return;
      }

      hasFiredImpressionRef.current = true;
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({ event: 'daycare_sticky_cta_impression', cta_position: ctaPosition });
    };

    el.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      el.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [visible, variant]);

  useEffect(() => {
    const handleScroll = () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      setVisible(false);

      const pastRevealPoint = window.scrollY > window.innerHeight * REVEAL_SCROLL_RATIO;
      if (!pastRevealPoint) {
        return;
      }

      idleTimerRef.current = setTimeout(() => {
        setVisible(true);
      }, IDLE_DELAY_MS);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const className = visible ? `${styles.base} ${styles.visible}` : styles.base;

  return (
    <div
      ref={wrapperRef}
      className={className}
      aria-hidden={!visible}
      inert={visible ? undefined : true}
      data-testid="sticky-line-cta"
    >
      {variant === 'line' ? (
        <LineButton position={LINE_CTA_POSITION}>{label ?? 'LINEで見学予約'}</LineButton>
      ) : (
        <Link href={PageUrl.DAYCARE_CONTACT} onClick={handleContactClick}>
          <MainButton
            isHoverEffect
            as="div"
            buttonText={label ?? '見学・体験予約'}
            fontSizeType={FontSize.MEDIUM}
            shape="rectangle"
            backgroundColor={Color.brandPink200}
            valiant="border"
          />
        </Link>
      )}
    </div>
  );
};
